import { RecordOf, hash } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import { coreActions } from '../core-actions';

import { QcsSyncResourceStoreProps } from '../core/resourceStoreTypes';
import * as accountsActions from './accountsActions';
import { AccountProps, AccountsStore, BaseAccountProps } from './accountsTypes';

export const QcsSyncAccountsStoreFactory = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<AccountProps>();
const reducer = handleActions<
  AccountsStore,
  | coreActions.SanitizeAction
  | ReturnType<typeof accountsActions.getAccounts>
  | ReturnType<typeof accountsActions.getAccountsSuccess>
  | ReturnType<typeof accountsActions.getAccountsFailure>
  | ReturnType<typeof accountsActions.updateAccount>
  | ReturnType<typeof accountsActions.applyAccountsChanges>
  | ReturnType<typeof accountsActions.batchAccounts>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map(
          (resource) =>
            (resource as RecordOf<BaseAccountProps>).merge({
              name: hash(resource.name).toString(),
              aggregators: resource.aggregators?.map((aggregator) =>
                aggregator.merge({
                  accountName: hash(aggregator.accountName).toString(),
                  accountNumberMasked: hash(aggregator.accountNumberMasked).toString(),
                }),
              ),
            }) as typeof resource,
        ),
      ),

    [accountsActions.getAccounts.type]: (state) => state.merge({ isLoading: true }),
    [accountsActions.getAccountsSuccess.type]: (state, { payload }) => {
      const { resources: accountsToUpsert } = payload;
      const accountsToUpsertExcludingIgnored = accountsToUpsert.filter((account) => !account.isIgnored);
      return resourceUpserters.upsertResources(
        state,
        payload.set('resources', accountsToUpsertExcludingIgnored as typeof accountsToUpsert),
      );
    },
    [accountsActions.getAccountsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError<AccountProps, QcsSyncResourceStoreProps<AccountProps>>(state, error),

    [accountsActions.updateAccount.type]: // optimistic update + undo
      (state, action) =>
        resourceUndoUpserters.upsertResourceSetupUndo<AccountProps, QcsSyncResourceStoreProps<AccountProps>>(
          state,
          action,
          accountsActions.updateAccount,
        ),

    [combineActions(
      // optimistic update
      accountsActions.createAccount,
      accountsActions.deleteAccount,
    ).toString()]: (state: any, { payload }: any) => resourceUpserters.upsertResource(state, payload),

    // optimistic update
    [accountsActions.batchAccounts.type]: (state, { payload: accountsToUpsert }) => {
      const accountsToUpsertExcludingIgnored = accountsToUpsert.filter((account) => !account.isIgnored);
      return resourceUpserters.upsertResources(state, {
        resources: accountsToUpsertExcludingIgnored as typeof accountsToUpsert,
      });
    },

    [accountsActions.applyAccountsChanges.type]: (state, { payload: accountsToUpsert, meta }) => {
      const accountsToUpsertExcludingIgnored = accountsToUpsert.filter((account) => !account.isIgnored);
      return resourceUpserters.upsertResources<AccountProps, QcsSyncResourceStoreProps<AccountProps>>(state, {
        resources: accountsToUpsertExcludingIgnored,
        doMerge: meta?.props?.doMerge ?? true,
      });
    },

    [combineActions(
      accountsActions.createAccountFailure,
      accountsActions.updateAccountFailure,
      accountsActions.deleteAccountFailure,
    ).toString()]: (state: any, { payload: error, asyncDispatch }: any) => {
      asyncDispatch(accountsActions.getAccounts());
      return resourceUpserters.resetResources(state, error);
    },
  },
  QcsSyncAccountsStoreFactory({ resourceName: 'ACCOUNTS' }),
);

export default reducer;
